/**
 * Route-level document head updates for SEO / share cards (no react-helmet).
 * Staging and non-production always force noindex; production respects `robots`.
 */

import { useEffect } from 'react';

const OG_IMAGE_PATH = '/og-devtools.png';
const JSON_LD_ATTR = 'data-devtools-jsonld';

export type PageRobots = 'index, follow' | 'noindex, nofollow';

export type PageMetaOptions = {
  title: string;
  description?: string;
  /** Path for canonical / og:url (e.g. `/for-agents`). Defaults to `/`. */
  path?: string;
  robots?: PageRobots;
  /** Absolute or site-relative OG image. Defaults to `/og-devtools.png`. */
  ogImage?: string;
  /** JSON-LD graph for this page; removed when null/undefined. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[] | null;
};

/** True only for production deploys — gates allowindex. */
export function isProductionSeo(): boolean {
  return import.meta.env.VITE_ENVIRONMENT === 'production';
}

/** Public site origin for canonical / OG URLs (prod default when env unset). */
export function getPublicSiteOrigin(): string {
  const fromEnv = (import.meta.env.VITE_APP_URL || '').trim().replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  return 'https://devtools.beatly.com';
}

function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const origin = getPublicSiteOrigin();
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${origin}${path}`;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function setJsonLd(data: PageMetaOptions['jsonLd']): void {
  document.head.querySelectorAll(`script[${JSON_LD_ATTR}]`).forEach((n) => n.remove());
  if (!data) return;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute(JSON_LD_ATTR, '1');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

/**
 * Apply title, description, robots, canonical, Open Graph, Twitter, and optional JSON-LD.
 * Non-production always uses `noindex, nofollow` regardless of `robots`.
 */
export function applyPageMeta(options: PageMetaOptions): void {
  // Staging / local always noindex even when a page asks for index, follow.
  const robots: PageRobots =
    isProductionSeo() && options.robots === 'index, follow' ? 'index, follow' : 'noindex, nofollow';

  const path = options.path ?? '/';
  const pageUrl = absoluteUrl(path === '' ? '/' : path);
  const description = options.description?.trim() || '';
  const ogImage = absoluteUrl(options.ogImage || OG_IMAGE_PATH);

  document.title = options.title;
  if (description) {
    upsertMeta('name', 'description', description);
  }
  upsertMeta('name', 'robots', robots);

  upsertLink('canonical', pageUrl);

  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:site_name', 'DevTools');
  upsertMeta('property', 'og:title', options.title);
  upsertMeta('property', 'og:url', pageUrl);
  upsertMeta('property', 'og:image', ogImage);
  if (description) {
    upsertMeta('property', 'og:description', description);
  }

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', options.title);
  upsertMeta('name', 'twitter:image', ogImage);
  if (description) {
    upsertMeta('name', 'twitter:description', description);
  }

  setJsonLd(options.jsonLd ?? null);
}

/** Keep document head in sync with the active route while mounted. */
export function usePageMeta(options: PageMetaOptions): void {
  const { title, description, path, robots, ogImage, jsonLd } = options;
  const jsonLdKey = jsonLd == null ? '' : JSON.stringify(jsonLd);

  useEffect(() => {
    applyPageMeta({
      title,
      description,
      path,
      robots,
      ogImage,
      jsonLd: jsonLdKey ? (JSON.parse(jsonLdKey) as PageMetaOptions['jsonLd']) : null,
    });
  }, [title, description, path, robots, ogImage, jsonLdKey]);
}
